import {
  rectIntersection,
  type CollisionDetection,
  type DragEndEvent,
  type DroppableContainer,
  type UniqueIdentifier,
} from "@dnd-kit/core";

export type ScheduleSwapKind = "slot" | "offline";

export type ScheduleSwapTarget = {
  kind: ScheduleSwapKind;
  storeKey: string;
};

export type ScheduleSwapDrop = {
  source: ScheduleSwapTarget;
  target: ScheduleSwapTarget;
  targetId: UniqueIdentifier;
};

// Drag ids must be unique in one drag context, so offline rows get a prefix.
export function offlineDragId(dateKey: string) {
  return `offline:${dateKey}`;
}

export function readSwapTarget(data: unknown): ScheduleSwapTarget | undefined {
  const isRecord = typeof data === "object" && data !== null;
  if (!isRecord) return undefined;
  const kind = "kind" in data ? data.kind : undefined;
  const storeKey = "storeKey" in data ? data.storeKey : undefined;
  const isKnownKind = kind === "slot" || kind === "offline";
  if (!isKnownKind) return undefined;
  const hasStoreKey = typeof storeKey === "string";
  if (!hasStoreKey) return undefined;
  return { kind, storeKey };
}

export function readSwapDrop(event: DragEndEvent): ScheduleSwapDrop | undefined {
  const over = event.over;
  if (!over) return undefined;
  const source = readSwapTarget(event.active.data.current);
  const target = readSwapTarget(over.data.current);
  const hasBothRows = source !== undefined && target !== undefined;
  if (!hasBothRows) return undefined;
  const isSameKind = source.kind === target.kind;
  if (!isSameKind) return undefined;
  const isSameRow = source.storeKey === target.storeKey;
  if (isSameRow) return undefined;
  return { source, target, targetId: over.id };
}

// Only rows the dragged row overlaps, of the same kind, can be targets.
export const sameKindCollision: CollisionDetection = (args) => {
  const activeTarget = readSwapTarget(args.active.data.current);
  const activeKind = activeTarget?.kind;
  const isSameKind = (container: DroppableContainer) => {
    const containerTarget = readSwapTarget(container.data.current);
    return containerTarget?.kind === activeKind;
  };
  const sameKindContainers = args.droppableContainers.filter(isSameKind);
  return rectIntersection({ ...args, droppableContainers: sameKindContainers });
};
